import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Icon
} from '@mui/material';
import {
  AddCircle,
  Edit,
  Delete,
  ToggleOn,
  ToggleOff
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import AppLayout from '../../components/layout/AppLayout';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import tipologieService from '../../services/tipologieService';
import type { TipologiaScadenza } from '../../types/tipologia.types';
import type { TableColumn } from '../../types/common.types';
import { getErrorMessage } from '../../utils/api';

const TipologieListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null
  });
  const [toggleDialog, setToggleDialog] = useState<{
    open: boolean;
    tipologia: TipologiaScadenza | null;
  }>({
    open: false,
    tipologia: null
  });

  // Filters
  const [filters, setFilters] = useState<{
    search: string;
    attiva: string;
  }>({
    search: '',
    attiva: ''
  });

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch tipologie
  const { data, isLoading } = useQuery({
    queryKey: ['tipologie', page, rowsPerPage, debouncedSearch, filters.attiva],
    queryFn: () =>
      tipologieService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch,
        attiva: filters.attiva === '' ? undefined : filters.attiva === 'true'
      })
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => tipologieService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipologie'] });
      setDeleteDialog({ open: false, id: null });
      enqueueSnackbar('Tipologia eliminata con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'eliminazione'), { variant: 'error' });
    }
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => tipologieService.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipologie'] });
      const tipologia = toggleDialog.tipologia;
      const newStatus = tipologia?.attiva ? 'disattivata' : 'attivata';
      setToggleDialog({ open: false, tipologia: null });
      enqueueSnackbar(`Tipologia ${newStatus} con successo`, { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante la modifica dello stato'), { variant: 'error' });
    }
  });

  const columns: TableColumn<TipologiaScadenza>[] = [
    {
      id: 'nome',
      label: 'Nome',
      minWidth: 180
    },
    {
      id: 'descrizione',
      label: 'Descrizione',
      minWidth: 250,
      format: (value) => {
        const desc = value as string | undefined;
        if (!desc) return '-';
        return desc.length > 60 ? `${desc.substring(0, 60)}...` : desc;
      }
    },
    {
      id: 'colore',
      label: 'Colore',
      minWidth: 100,
      align: 'center',
      format: (value) => {
        const color = value as string | undefined;
        if (!color) return '-';
        return (
          <Box
            sx={{
              width: 30,
              height: 30,
              backgroundColor: color,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: 'divider',
              margin: '0 auto'
            }}
          />
        );
      }
    },
    {
      id: 'icona',
      label: 'Icona',
      minWidth: 80,
      align: 'center',
      format: (value) => {
        const icona = value as string | undefined;
        if (!icona) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        return (
          <Icon sx={{ fontSize: 32, color: 'text.secondary' }}>
            {icona}
          </Icon>
        );
      }
    },
    {
      id: 'giorniPreavvisoDefault',
      label: 'Giorni Preavviso',
      minWidth: 140,
      align: 'center'
    },
    {
      id: 'attiva',
      label: 'Stato',
      minWidth: 120,
      format: (_, row) => (
        <Chip
          label={row.attiva ? 'Attiva' : 'Non Attiva'}
          color={row.attiva ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ];

  const handleDelete = (id: string) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = () => {
    if (deleteDialog.id) {
      deleteMutation.mutate(deleteDialog.id);
    }
  };

  const handleToggleActive = (tipologia: TipologiaScadenza) => {
    setToggleDialog({ open: true, tipologia });
  };

  const confirmToggleActive = () => {
    if (toggleDialog.tipologia) {
      toggleActiveMutation.mutate(toggleDialog.tipologia.id);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const renderActions = (row: TipologiaScadenza) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Tooltip title="Modifica">
        <IconButton size="small" onClick={() => navigate(`/tipologie/${row.id}/edit`)}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={row.attiva ? 'Disattiva' : 'Attiva'}>
        <IconButton
          size="small"
          color={row.attiva ? 'warning' : 'success'}
          onClick={() => handleToggleActive(row)}
          disabled={toggleActiveMutation.isPending}
        >
          {row.attiva ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Elimina">
        <IconButton
          size="small"
          color="error"
          onClick={() => handleDelete(row.id)}
          disabled={deleteMutation.isPending}
        >
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (isLoading && !data) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento tipologie..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2
        }}>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Gestione Tipologie Scadenze
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddCircle />}
            onClick={() => navigate('/tipologie/new')}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Nuova Tipologia
          </Button>
        </Box>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filtri
          </Typography>
          <form onSubmit={(e) => e.preventDefault()}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: { xs: 2, md: 3 }
            }}>
              <TextField
                label="Cerca"
                placeholder="Nome tipologia..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                size="small"
                fullWidth
                helperText={filters.search !== debouncedSearch ? "Ricerca in corso..." : undefined}
              />
              <TextField
                select
                label="Stato"
                value={filters.attiva}
                onChange={(e) => handleFilterChange('attiva', e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">Tutte</MenuItem>
                <MenuItem value="true">Attive</MenuItem>
                <MenuItem value="false">Non Attive</MenuItem>
              </TextField>
            </Box>
          </form>
        </Paper>

        <DataTable
          columns={columns}
          data={data?.data || []}
          total={data?.total || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          actions={renderActions}
          loading={isLoading}
          emptyStateComponent={
            <EmptyState
              title="Nessuna tipologia trovata"
              message={
                debouncedSearch || filters.attiva
                  ? "Non ci sono tipologie che corrispondono ai filtri selezionati. Prova a modificare i criteri di ricerca."
                  : "Non hai ancora creato nessuna tipologia. Le tipologie ti aiutano a categorizzare le scadenze. Inizia creando la tua prima tipologia."
              }
              actionLabel={!debouncedSearch && !filters.attiva ? "Crea Prima Tipologia" : undefined}
              onAction={!debouncedSearch && !filters.attiva ? () => navigate('/tipologie/new') : undefined}
            />
          }
        />

        <ConfirmDialog
          open={deleteDialog.open}
          title="Conferma Eliminazione"
          message="Sei sicuro di voler eliminare questa tipologia? Questa azione non puo essere annullata."
          confirmText="Elimina"
          confirmColor="error"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteDialog({ open: false, id: null })}
          loading={deleteMutation.isPending}
        />

        <ConfirmDialog
          open={toggleDialog.open}
          title={`Conferma ${toggleDialog.tipologia?.attiva ? 'Disattivazione' : 'Attivazione'}`}
          message={`Sei sicuro di voler ${toggleDialog.tipologia?.attiva ? 'disattivare' : 'attivare'} la tipologia "${toggleDialog.tipologia?.nome}"?`}
          confirmText={toggleDialog.tipologia?.attiva ? 'Disattiva' : 'Attiva'}
          confirmColor={toggleDialog.tipologia?.attiva ? 'warning' : 'success'}
          onConfirm={confirmToggleActive}
          onCancel={() => setToggleDialog({ open: false, tipologia: null })}
          loading={toggleActiveMutation.isPending}
        />
      </Box>
    </AppLayout>
  );
};

export default TipologieListPage;
